import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { haeBrandi, type Brandi } from "./brandi";
import { t } from "./kieli";

/**
 * Tulosteen (PDF) leima: talon tunnus ja instanssin merkintä.
 *
 * Tuloste piirtyy `body:has(.tuloste)`-säännöllä niin että KAIKKI
 * `.tuloste`n ulkopuolinen on näkymätöntä — myös ylapalkki ja sen
 * merkintä. Demosta otettu tarjous-PDF kiertää ilman kehystään, ja
 * juuri siinä merkinnän on oltava (Toni 10.9.2026: «merkinnän on
 * näyttävä myös PDF:ssä»). Siksi merkintä on tässä, tulosteen sisällä,
 * eikä vain ylapalkissa.
 *
 * Yksi komponentti kahdelle tulosteelle (Tarjous, Osaaminen): leima ja
 * merkintä olivat kummassakin kopioina, ja merkintä olisi pitänyt
 * lisätä kahteen paikkaan — kopio ei kuuntele lähdettä.
 */
export function TulosteLeima({ brandi }: { brandi: Brandi | null }) {
  return (
    <div className="tuloste__leimat">
      <div className="tuloste__leima">{brandi?.tunnus ?? brandi?.nimi ?? "SAMPO"}</div>
      {brandi?.merkinta && (
        <div className="tuloste__merkinta" data-merkinta={brandi.merkinta}>{brandi.merkinta}</div>
      )}
    </div>
  );
}

/** Teksti CSS-merkkijonoksi (content: "…"): kenoviiva ja lainausmerkki koodataan, rivinvaihto välilyönniksi. */
const cssMerkkijono = (s: string) => s.replace(/\\/g, "\\\\").replace(/"/g, "\\\"").replace(/[\r\n]+/g, " ");

/**
 * Tulosteen yhteinen pohja (T635, Toni 20.10.2026 klo 07.06: «tulostuksen yhteinen pohja on ydin, jotta sama palvelee myöhemmin muita
 * raportteja — painekoe, luovutus»). Näytön päällinen kerros työkaluineen (Sulje, Tulosta), A4-pystysivu, otsakkeessa talon leima ja
 * instanssin merkintä (TulosteLeima), otsikko ja metatiedot oikealla. Sisältö on raportin oma; pohja ei tiedä moduuleista mitään.
 * A4-pysty `.tuloste--a4`:llä (tyylit.css): yleinen @page on A3 vaaka virallista hitsauslokia varten.
 */
export function TulostePohja({ otsikko, meta, sulje, tunniste, merkinta, children }: {
  otsikko: string; meta?: ReactNode; sulje: () => void;
  /** data-tuloste-arvo — kävely ja testit tunnistavat raportin lajin. */
  tunniste: string;
  /** Keskeneräisen raportin merkintä (T635, Toni 22.10.2026 klo 11.45): musta palkki ensimmäisen sivun alussa ja sama teksti jokaisen
   *  sivun alareunassa sivunumeroineen. Sivun marginaalilaatikko (@page @bottom-center), ei position: fixed: kiinteä elementti
   *  peittää sisältöä ja katosi ensimmäiseltä sivulta, kun se siirrettiin marginaaliin (koe palvelimen chromiumilla 22.10.: 0/1 · 1/1 · 1/1). */
  merkinta?: string; children: ReactNode;
}) {
  const [brandi, setBrandi] = useState<Brandi | null>(null);
  useEffect(() => { void haeBrandi().then(setBrandi).catch(() => setBrandi(null)); }, []);
  // Portaali suoraan bodyn alle: tulosteessa muu sovellus poistetaan (display: none, tyylit.css), ei vain piiloteta. Ennen tätä raportti oli
  // sovelluksen sisällä, ja visibility: hidden jätti sovelluksen tilan paperille — esimerkki-PDF 20.10.: 1 sivu raporttia + 5 tyhjää sivua.
  return createPortal(
    <div className="tuloste-overlay tuloste-portaali">
      <div className="tuloste-tyokalut">
        <button className="nappi nappi--kevyt" onClick={sulje}>{t("tuloste.sulje")}</button>{" "}
        <button className="nappi nappi--paa" data-tuloste-tulosta="" onClick={() => window.print()}>{t("tuloste.tulosta")}</button>
      </div>
      <article className="tuloste tuloste--a4" data-tuloste-laji={tunniste}>
        {merkinta && <>
          <style>{`@page a4pysty { @bottom-center { content: "${cssMerkkijono(merkinta)} · " counter(page) " / " counter(pages); font: 800 8pt sans-serif; color: #000; } }`}</style>
          <p className="tuloste__luonnos" data-tuloste-merkinta="">{merkinta}</p>
        </>}
        <header className="tuloste__paa">
          <TulosteLeima brandi={brandi} />
          <div className="tuloste__otsikko">
            <h1>{otsikko}</h1>
            {meta && <div className="tuloste__meta">{meta}</div>}
          </div>
        </header>
        {children}
      </article>
    </div>,
    document.body,
  );
}
